import { useMemo, useCallback } from 'react';
import _get from 'lodash/get';

import { useGetConfiguration } from '@common/configuration';
import { useSession } from '@common/contexts/session';
import useAuth from '@common/hooks/use-auth';

import { V2_ROUTE_CONFIG } from '@aware/routes';

export const useGetRoutes = () => {
  /**
   * For top level nav items, FE does not need to do any feature flag checks to show/hide a page.
   * The Permissions API should be performing that check before returning data.
   * Note: For settings pages, the FE performs feature flag checks, since these are not part of the permissions API.
   * (see https://github.com/eduphoria/frontend/blob/main/src/aware/hooks.js#L47)
   */
  const { permissions: permissionsByArea } = useSession();
  const configuration = useGetConfiguration('aware');
  const { checkDistrictLevelRights, checkRights } = useAuth();

  /**
   * We're going to loop over the settings array from routes config and remove routes for rights
   * checks, permissions, or feature flags check that return false. We'll use this to conditionally
   * display the settings in the navigation menu
   */
  const getFilteredSettings = useCallback(
    settingPaths =>
      settingPaths.length
        ? settingPaths
            .filter(setting => {
              if (!setting.rights) return true;

              return setting.rights.some(({ right, districtLevel, campusIds }) => {
                return districtLevel
                  ? checkDistrictLevelRights(right)
                  : checkRights(right, campusIds);
              });
            })
            .filter(setting => {
              if (!setting.permissions) return true;

              return setting.permissions.some(
                permissionPath => _get(permissionsByArea, permissionPath) === true
              );
            })
            .filter(setting => {
              return !setting.featureFlagCheck || setting.featureFlagCheck(configuration);
            })
        : [],
    [checkDistrictLevelRights, checkRights, configuration, permissionsByArea]
  );

  return useMemo(() => {
    /**
     * Maps permissions to routes
     *
     * Returns an object of routes that is used to build main navigation
     */
    const routesByArea = Object.keys(permissionsByArea)
      .filter(area => permissionsByArea[area].visible) // filter any areas that aren't visible
      .reduce((acc, area) => {
        const { navigation: navigationForArea = {}, ...rest } = permissionsByArea[area];

        const viewableNavigation = Object.keys(navigationForArea)
          .filter(page => navigationForArea[page]) // filter any pages where value is false
          .reduce((subAcc, page) => {
            const routeConfig = _get(V2_ROUTE_CONFIG, [area, page]);

            return { ...subAcc, [page]: routeConfig };
          }, {});

        const areaSettingsConfig = _get(V2_ROUTE_CONFIG, [area, 'settings'], []);

        const viewableSettings = areaSettingsConfig?.length
          ? areaSettingsConfig.reduce((subAcc, settingSection) => {
              const filteredPaths = getFilteredSettings(settingSection.paths);

              // Only add to subAcc if there are any filteredPaths
              if (filteredPaths.length) {
                return [...subAcc, { ...settingSection, paths: filteredPaths }];
              }

              return subAcc;
            }, [])
          : [];

        return {
          ...acc,
          [area]: {
            ...rest,
            title: V2_ROUTE_CONFIG[area]?.title,
            settings: viewableSettings,
            navigation: viewableNavigation,
            breadcrumb: V2_ROUTE_CONFIG[area]?.breadcrumb
          }
        };
      }, {});

    /**
     * Reformat routesByArea as an array
     * Used to build breadcrumbs and by react-router to create routes
     */

    const allRoutes = [
      ...Object.values(routesByArea).flatMap(area => {
        const navigationRoutes = Object.values(area.navigation)
          .filter(route => route) // remove undefined routes
          .map(route => route);

        // Get all settings paths
        const settingsRoutes = area.settings.flatMap(({ paths }) => paths);

        /**
         * Combine navigation routes and settings to that react-router can auto-generate routes
         * Because we have routes nested under areas, but not every area has a homepage yet (e.g. Item Bank),
         * it's important that settings are first, so that they'll match before any other navigation routes.
         *
         * Also adding separate routes so that areas can specify their own breadcrumbs. If I don't do this,
         * useReactRouterBreadcrumbs will add spaces between titles, so Item Bank becomes "Item-bank"
         */
        const combinedRoutes = [...settingsRoutes, ...navigationRoutes];
        return area.breadcrumb
          ? [
              ...combinedRoutes,
              { path: area.breadcrumb.path, breadcrumb: area.breadcrumb.title }
            ]
          : combinedRoutes;
      })
    ];

    return { routesByArea, allRoutes };
  }, [getFilteredSettings, permissionsByArea]);
};
